import React, { useEffect, useRef } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Grid2,
  IconButton,
  Paper,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { observer, useLocalObservable } from "mobx-react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import ImageAPI from "../../../api/ImageAPI";
import { CatalogProductStore } from "../../../stores/CatalogProductStore";
import {
  ProductVariantApi,
  UpdateVariantImagesInput,
  getApiErrorMessage,
} from "../../../../../models/CatalogProducts/types";
import { useNotification } from "../../../../../utils/NotificationsContext";

interface DraftImage {
  clientKey: string;
  id?: number;
  image_url: string;
  active: number;
  /** Local preview for newly picked files before upload completes. */
  previewUrl?: string;
  pendingFile?: File;
}

interface GalleryImage {
  id?: number;
  src: string;
  alt?: string;
}

interface ProductVariantImagesEditorProps {
  productId: number;
  store: CatalogProductStore;
  disabled?: boolean;
  onPreviewImages?: (
    images: GalleryImage[],
    initialIndex: number,
    title?: string,
  ) => void;
}

function variantLabel(v: ProductVariantApi): string {
  const parts = [
    v.option_name,
    v.title || v.name,
    v.sku ? `SKU ${v.sku}` : null,
  ].filter(Boolean);
  return parts.join(" · ") || `Variant #${v.id}`;
}

function activeImageCount(v: ProductVariantApi): number {
  return (v.images ?? []).filter((img) => img.active).length;
}

const ProductVariantImagesEditor: React.FC<ProductVariantImagesEditorProps> = ({
  productId,
  store,
  disabled = false,
  onPreviewImages,
}) => {
  const theme = useTheme();
  const { showError, showSuccess } = useNotification();
  const colors = theme.palette.customColors;
  const imageAPI = useRef(new ImageAPI()).current;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const ui = useLocalObservable(() => ({
    selectedVariantId: null as number | null,
    draftImages: [] as DraftImage[],
    dirty: false,
    uploading: false,
    saving: false,
    hoveredImageKey: null as string | null,
    selectVariant(variant: ProductVariantApi) {
      this.selectedVariantId = variant.id;
      this.draftImages = (variant.images ?? [])
        .filter((img) => img.active)
        .map((img) => ({
          clientKey: `existing-${img.id}`,
          id: img.id,
          image_url: img.image_url,
          active: 1,
        }));
      this.dirty = false;
    },
    removeImage(clientKey: string) {
      const removed = this.draftImages.find((img) => img.clientKey === clientKey);
      if (removed?.previewUrl) {
        try {
          URL.revokeObjectURL(removed.previewUrl);
        } catch {
          // Ignore revoke failures; draft update still proceeds.
        }
      }
      this.draftImages = this.draftImages.filter(
        (img) => img.clientKey !== clientKey,
      );
      this.dirty = true;
    },
    addPendingFiles(files: File[]) {
      const next = files.map((file, idx) => ({
        clientKey: `pending-${Date.now()}-${idx}-${file.name}`,
        image_url: "",
        active: 1,
        previewUrl: URL.createObjectURL(file),
        pendingFile: file,
      }));
      this.draftImages = [...this.draftImages, ...next];
      this.dirty = true;
    },
    setHoveredImageKey(key: string | null) {
      this.hoveredImageKey = key;
    },
  }));

  const variants = (store.productDetail?.variants ?? []).filter((v) => v.active);
  const selected =
    variants.find((v) => v.id === ui.selectedVariantId) ?? null;

  useEffect(() => {
    if (!ui.selectedVariantId && variants.length > 0) {
      ui.selectVariant(variants[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants.map((v) => v.id).join(",")]);

  useEffect(() => {
    if (!ui.selectedVariantId) return;
    const refreshed = variants.find((v) => v.id === ui.selectedVariantId);
    if (refreshed && !ui.dirty) {
      ui.selectVariant(refreshed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.productDetail?.variants]);

  const handlePickFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;
    ui.addPendingFiles(Array.from(files));
    event.target.value = "";
  };

  const handleSave = async () => {
    if (!ui.selectedVariantId || disabled) return;
    try {
      ui.saving = true;
      ui.uploading = true;

      const uploaded: DraftImage[] = [];
      for (const img of ui.draftImages) {
        if (img.pendingFile) {
          const urls = await imageAPI.uploadImage(
            [img.pendingFile],
            "product-images",
          );
          const url = urls?.[0];
          if (!url) {
            throw new Error("Upload did not return an image URL.");
          }
          if (img.previewUrl) {
            try {
              URL.revokeObjectURL(img.previewUrl);
            } catch {
              // Ignore revoke failures after successful upload.
            }
          }
          uploaded.push({
            clientKey: img.clientKey,
            image_url: url,
            active: 1,
          });
        } else if (img.image_url) {
          uploaded.push({
            clientKey: img.clientKey,
            id: img.id,
            image_url: img.image_url,
            active: 1,
          });
        }
      }

      const keptIds = new Set(
        uploaded
          .map((img) => img.id)
          .filter((id): id is number => typeof id === "number" && id > 0),
      );
      // Backend soft-deletes via active: 0; omitted ids are left unchanged.
      const removedPayload: UpdateVariantImagesInput["images"] = (
        selected?.images ?? []
      )
        .filter((img) => img.active && img.id > 0 && !keptIds.has(img.id))
        .map((img) => ({
          id: img.id,
          image_url: img.image_url,
          active: 0,
        }));

      const body: UpdateVariantImagesInput = {
        images: [
          ...uploaded.map((img) => ({
            ...(img.id ? { id: img.id } : {}),
            image_url: img.image_url,
            active: 1,
          })),
          ...removedPayload,
        ],
      };

      ui.uploading = false;
      await store.updateVariantImages(productId, ui.selectedVariantId, body);
      ui.dirty = false;
      const refreshed = store.productDetail?.variants.find(
        (v) => v.id === ui.selectedVariantId,
      );
      if (refreshed) ui.selectVariant(refreshed);
      showSuccess("Variant images saved.");
    } catch (error) {
      showError(
        "Error",
        getApiErrorMessage(error, "Could not save variant images."),
      );
    } finally {
      ui.uploading = false;
      ui.saving = false;
    }
  };

  if (variants.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, borderColor: "divider" }}
      >
        <CustomTypography variant="body3" color={colors?.greyTextColor}>
          Save variants first, then attach images to each variant here.
        </CustomTypography>
      </Paper>
    );
  }

  return (
    <Grid2 container spacing={2} columns={12}>
      <Grid2 size={{ xs: 12 }}>
        <CustomTypography variant="body3" color={colors?.greyTextColor}>
          Click a variant, then upload or remove images. Save applies only to
          the selected variant.
        </CustomTypography>
      </Grid2>

      <Grid2 size={{ xs: 12 }}>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {variants.map((v) => {
            const selectedChip = v.id === ui.selectedVariantId;
            const count = activeImageCount(v);
            return (
              <Chip
                key={v.id}
                clickable={!disabled}
                onClick={() => {
                  if (disabled) return;
                  if (ui.dirty && ui.selectedVariantId !== v.id) {
                    // Switch without blocking — draft is per selection reset
                  }
                  ui.selectVariant(v);
                }}
                color={selectedChip ? "primary" : "default"}
                variant={selectedChip ? "filled" : "outlined"}
                label={
                  <Box display="inline-flex" alignItems="center" gap={0.75}>
                    {v.hex_code ? (
                      <Box
                        component="span"
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: v.hex_code,
                          border: `1px solid ${colors?.greyBorder}`,
                          display: "inline-block",
                        }}
                      />
                    ) : null}
                    {variantLabel(v)}
                    <Box
                      component="span"
                      sx={{
                        ml: 0.5,
                        px: 0.75,
                        borderRadius: 1,
                        fontSize: "0.7rem",
                        bgcolor: selectedChip
                          ? alpha("#fff", 0.2)
                          : alpha(
                              colors?.purpleMain || theme.palette.primary.main,
                              0.08,
                            ),
                      }}
                    >
                      {count}
                    </Box>
                  </Box>
                }
                sx={{
                  height: "auto",
                  py: 0.75,
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    display: "block",
                  },
                }}
              />
            );
          })}
        </Box>
      </Grid2>

      {selected ? (
        <Grid2 size={{ xs: 12 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: "divider",
              backgroundColor: alpha(
                colors?.purpleMain || theme.palette.primary.main,
                0.02,
              ),
            }}
          >
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              mb={2}
            >
              <Box>
                <CustomTypography variant="body2" fontWeight={600}>
                  {variantLabel(selected)}
                </CustomTypography>
                <CustomTypography
                  variant="caption"
                  color={colors?.greyTextColor}
                >
                  {ui.draftImages.length} image
                  {ui.draftImages.length === 1 ? "" : "s"}
                  {ui.dirty ? " · unsaved changes" : ""}
                </CustomTypography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <AppButton
                  variant="secondary"
                  label="Add images"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || ui.saving}
                />
                <AppButton
                  variant="primary"
                  label={ui.uploading ? "Uploading…" : "Save images"}
                  onClick={() => {
                    void handleSave();
                  }}
                  disabled={disabled || ui.saving || !ui.dirty}
                />
              </Box>
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handlePickFiles}
            />

            {ui.saving ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} />
              </Box>
            ) : ui.draftImages.length === 0 ? (
              <Box
                onClick={() => {
                  if (!disabled) fileInputRef.current?.click();
                }}
                sx={{
                  border: `1px dashed ${colors?.greyBorder}`,
                  borderRadius: 2,
                  py: 5,
                  px: 2,
                  textAlign: "center",
                  cursor: disabled ? "default" : "pointer",
                  "&:hover": disabled
                    ? undefined
                    : {
                        borderColor:
                          colors?.purpleMain || theme.palette.primary.main,
                        backgroundColor: alpha(
                          colors?.purpleMain || theme.palette.primary.main,
                          0.04,
                        ),
                      },
                }}
              >
                <CloudUploadOutlinedIcon
                  sx={{
                    fontSize: 36,
                    color: colors?.greyTextColor,
                    mb: 1,
                  }}
                />
                <CustomTypography variant="body2" fontWeight={600}>
                  Drop images here or click to upload
                </CustomTypography>
                <CustomTypography
                  variant="caption"
                  color={colors?.greyTextColor}
                >
                  JPG, PNG, or WebP — multiple files supported
                </CustomTypography>
              </Box>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1.5}>
                {ui.draftImages.map((img, idx) => {
                  const src = img.previewUrl || img.image_url;
                  return (
                    <Paper
                      key={img.clientKey}
                      variant="outlined"
                      sx={{
                        width: 132,
                        p: 1,
                        borderRadius: 2,
                        position: "relative",
                        borderColor: img.pendingFile
                          ? colors?.purpleMain
                          : "divider",
                      }}
                    >
                      <Box
                        sx={{ position: "relative" }}
                        onMouseEnter={() =>
                          ui.setHoveredImageKey(img.clientKey)
                        }
                        onMouseLeave={() => ui.setHoveredImageKey(null)}
                      >
                        <Box
                          component="img"
                          src={src}
                          alt=""
                          sx={{
                            width: "100%",
                            height: 96,
                            objectFit: "cover",
                            borderRadius: 1,
                            display: "block",
                            bgcolor: colors?.greyBackground,
                          }}
                        />
                        {src && ui.hoveredImageKey === img.clientKey ? (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              backgroundColor: "rgba(0, 0, 0, 0.5)",
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              try {
                                e.stopPropagation();
                                if (!onPreviewImages) return;
                                const galleryImages: GalleryImage[] =
                                  ui.draftImages.flatMap((row, i) => {
                                    const rowSrc =
                                      row.previewUrl || row.image_url;
                                    if (!rowSrc) return [];
                                    const image: GalleryImage = {
                                      src: rowSrc,
                                      alt: `${variantLabel(selected)} image ${i + 1}`,
                                    };
                                    if (row.id !== undefined) {
                                      image.id = row.id;
                                    }
                                    return [image];
                                  });
                                const startIndex = galleryImages.findIndex(
                                  (row) => row.src === src,
                                );
                                onPreviewImages(
                                  galleryImages,
                                  startIndex >= 0 ? startIndex : idx,
                                  variantLabel(selected),
                                );
                              } catch {
                                // Ignore; remove/upload actions remain available.
                              }
                            }}
                            aria-label={`View variant image ${idx + 1}`}
                          >
                            <VisibilityOutlinedIcon
                              sx={{
                                color: colors?.white || "#fff",
                                fontSize: 20,
                              }}
                            />
                          </Box>
                        ) : null}
                      </Box>
                      {img.pendingFile ? (
                        <Chip
                          size="small"
                          label="New"
                          color="primary"
                          sx={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            height: 20,
                            fontSize: "0.65rem",
                            zIndex: 1,
                          }}
                        />
                      ) : null}
                      {!disabled ? (
                        <Tooltip title="Remove image">
                          <IconButton
                            size="small"
                            onClick={() => ui.removeImage(img.clientKey)}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              zIndex: 1,
                              bgcolor: "background.paper",
                              border: `1px solid ${colors?.greyBorder}`,
                              "&:hover": {
                                bgcolor: "background.paper",
                                color: colors?.errorRed,
                              },
                            }}
                            aria-label="Remove image"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Paper>
                  );
                })}
                {!disabled ? (
                  <Paper
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      width: 132,
                      height: 128,
                      borderRadius: 2,
                      borderStyle: "dashed",
                      borderColor: colors?.greyBorder,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 0.5,
                      cursor: "pointer",
                      "&:hover": {
                        borderColor:
                          colors?.purpleMain || theme.palette.primary.main,
                      },
                    }}
                  >
                    <CloudUploadOutlinedIcon
                      sx={{ color: colors?.greyTextColor }}
                    />
                    <CustomTypography
                      variant="caption"
                      color={colors?.greyTextColor}
                    >
                      Add more
                    </CustomTypography>
                  </Paper>
                ) : null}
              </Box>
            )}
          </Paper>
        </Grid2>
      ) : null}
    </Grid2>
  );
};

export default observer(ProductVariantImagesEditor);
